import useAuthStore from '@/stores/authStore'
import { computed, ref } from 'vue'
import { watchOnce } from '@vueuse/core'

/**
 * One-shot reason for the most recent implicit logout. Set by whoever clears
 * the tokens (e.g. useIdleTimeout → 'inactivity'), consumed and reset by
 * useAuthEffects when it redirects to /authenticate — the Authenticate view
 * shows a message for ?reason=inactivity (legacy behavior preserved).
 */
export const logoutReason = ref<string | null>(null)

/**
 * Single refresh implementation for the whole app. Called from:
 * - utils/api.ts authMiddleware (new openapi-fetch layer)
 * - src_old api_mixin get_valid_token (legacy hand-written fetch layer)
 *
 * All reset paths use authStore.clearAllTokens() consistently so the
 * Vuex bridge getters behave deterministically regardless of which path
 * cleared the tokens.
 */
export async function refreshToken(): Promise<string | null> {
  const authStore = useAuthStore()

  // Safari <15.4 lacks navigator.locks — refresh without the mutex rather than
  // crash. Duplicate refreshes in a race are idempotent against the API.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (navigator.locks == null) {
    if (authStore.selectedToken?.payload == null || !('exp' in authStore.selectedToken.payload)) return null
    return await performRefresh()
  }

  return await navigator.locks.request('refresh_token', { ifAvailable: true }, async (lock) => {
    if (authStore.selectedToken?.payload == null || !('exp' in authStore.selectedToken.payload)) return null
    console.log('Refreshing token...')

    // we don't have the lock, so someone else is refreshing the token
    if (lock == null) {
      // wait for selectedToken to change or exp for selected token is in the future
      if (authStore.selectedToken.payload.exp * 1000 > Date.now()) {
        return authStore.selectedToken.token
      }
      return Promise.any([
        new Promise<string | null>((resolve) => {
          watchOnce(
            () => authStore.selectedToken?.token,
            () => {
              console.log('Got new token')
              resolve(authStore.selectedToken?.token ?? null)
            }
          )
        }),
        new Promise<null>((resolve) =>
          setTimeout(() => {
            resolve(null)
          }, 5 * 1000)
        ),
      ])
    }

    return await performRefresh()
  })
}

async function performRefresh(): Promise<string | null> {
  const authStore = useAuthStore()
  const currentToken = authStore.selectedToken?.token
  if (currentToken == null) return null

  try {
    const res = await fetch(new URL('authenticates/refresh-token', import.meta.env.VITE_APP_API_ROOT), {
      method: 'POST',
      headers: { 'x-api-key': currentToken },
    })
    if (!res.ok) {
      console.error('Failed to refresh token', `${res.status} ${res.statusText}`, await res.text())
      authStore.clearAllTokens()
      return null
    }

    const body = (await res.json()) as { token: string }
    authStore.updateCurrentToken(body)
    return authStore.selectedToken?.token ?? null
  } catch {
    // Transient network failure — don't clear tokens; the caller decides.
    return null
  }
}

export function useAuth() {
  const authStore = useAuthStore()

  function signOut() {
    authStore.clearAllTokens()
  }

  // customer-ui has no pre-auth token concept in the store — authenticated
  // simply means a token is selected (route guard gates on this).
  const isAuthenticated = computed(() => authStore.selectedToken != null)

  return {
    signOut,
    isAuthenticated,

    selectedToken: computed(() => authStore.selectedToken),
    tokensList: computed(() => authStore.tokensList),
  }
}
